<template>
  
  <div>

    <div class="ui large breadcrumb">
      <back-button-link label="Fechamentos" to="/financial/customer-invoices" />
      <i class="right chevron icon divider"></i>
      <div class="bold section">
        Visualizar Fechamento de Cliente
      </div>
    </div>

    <div class="ui divider"></div>

    <div v-if="loading"><i class="spinner circle loading blue icon"></i> Carregando ...</div> 

    <div v-else class="animated fadeIn">

      <a class="ui button" :href="pdfLink" target="_blank">
        <i class="print blue icon"></i> Imprimir
      </a>

      <button v-if="isWaiting" class="ui button m-l-10" 
        :class="{ loading: updatingStatus }" @click="$refs.confirmStatusUpdateModal.show()">
        <i class="check circle green icon"></i> Aprovar
      </button>

      <button v-if="isApproved" class="ui button m-l-10" 
        :class="{ loading: updatingStatus }" @click="$refs.confirmStatusUpdateModal.show()">
        <i class="clock outline orange icon"></i> Não aprovado
      </button>

      <button v-if="isWaiting" class="ui button m-l-10" :class="{ loading: removing }" @click="$refs.confirmRemovalModal.show()">
        <i class="times red icon"></i> Excluir
      </button>

      <div class="ui segment m-t-20">
        <h4 class="ui dividing header">Dados do Fechamento</h4>
        <div class="ui form">
          <div class="fields">
            <div class="two wide field">
              <label class="form-label">Número</label>
              {{ invoice.id }}
            </div>
            <div class="three wide field">
              <label class="form-label">Status</label>
              <span v-html="invoice.statusData.label"></span>
            </div>
            <div class="two wide field">
              <label class="form-label">Data Criação</label>
              {{ $formatDate(invoice.date) }}
            </div>
            <div class="two wide field">
              <label class="form-label">Data Inicial</label>
              {{ $formatDate(invoice.startDate) }}
            </div>
            <div class="two wide field">
              <label class="form-label">Data Final</label>
              {{ $formatDate(invoice.endDate) }}
            </div>                
            <div class="five wide field">
              <label class="form-label">Cliente</label>
              {{ invoice.customer.shortName }}
            </div>
          </div>
          <div class="fields">
            <div class="two wide field">
              <label class="form-label">Laudos</label>
              {{ invoice.certs.length }}
            </div>
            <div class="three wide field">
              <label class="form-label">Valor do Fechamento</label>
              R$ {{ $formatCurrency(invoice.value) }}
            </div>                                  
          </div>
        </div>
      </div>

      <div v-if="invoice.value && invoice.status == 'waiting'" class="ui segment">
        <h4 class="ui dividing header">Vencimentos</h4>
        <div class="ui grid">
          <div class="eight wide computer sixteen wide tablet sixteen wide mobile column">
            <table class="ui celled selectable tablet stackable table">
              <thead v-if="$isDesktop">
                <tr>
                  <th>Dia da Semana</th>
                  <th>Vencimento</th>
                  <th>Valor</th>
                </tr>  
              </thead>
              <tbody>
                <tr v-for="(receivable, index) in invoice.beforeApprovalReceivables">
                  <td>
                    <strong class="mobile-only">Dia da Semana: </strong> {{ weekDayLabel(receivable.dueDate) }}
                  </td>
                  <td>
                    <strong class="mobile-only">Vencimento: </strong> {{ $formatDate(receivable.dueDate) }}   
                  </td>
                  <td>
                    <strong class="mobile-only">Valor: </strong> R$ {{ $formatCurrency(receivable.value) }}
                  </td>                  
                </tr>                
              </tbody>              
            </table>
          </div>
        </div>
      </div>

      <div v-if="invoice.value && invoice.status == 'approved'" class="ui segment">
        <h4 class="ui dividing header">Títulos a Receber</h4>
        <div class="ui grid">
          <div class="sixteen wide computer sixteen wide tablet sixteen wide mobile column">
            <table class="ui celled selectable tablet stackable table">
              <thead v-if="$isDesktop">
                <tr>
                  <th>Status</th>
                  <th>Tipo</th>
                  <th>Número</th>
                  <th>Vencimento</th>
                  <th>Valor</th>
                  <th>Saldo</th>
                  <th></th>
                </tr>  
              </thead>
              <tbody>
                <tr v-for="(receivable, index) in invoice.receivables" :class="{ error: receivable.isExpired }">
                  <td>
                    <strong class="mobile-only">Status: </strong>
                    <span v-html="receivable.statusData.label"></span>
                  </td>
                  <td>
                    <strong class="mobile-only">Tipo: </strong>
                    <span v-html="receivable.typeData.label"></span>
                  </td>
                  <td>
                    <strong class="mobile-only">Número: </strong> {{ receivable.id }}
                  </td>
                  <td>
                    <strong class="mobile-only">Vencimento: </strong> {{ $formatDate(receivable.dueDate) }}   
                  </td>
                  <td>
                    <strong class="mobile-only">Valor: </strong> R$ {{ $formatCurrency(receivable.value) }}
                  </td>
                  <td>
                    <strong class="mobile-only">Saldo: </strong> R$ {{ $formatCurrency(receivable.balance) }}
                  </td>
                  <td class="details-cell">
                    <button class="ui small icon button" @click="$router.push(`/financial/receivables/${receivable.id}?historyBack=true`)">
                      <i class="search plus icon"></i>
                    </button>
                  </td>
                </tr>                
              </tbody>              
            </table>
          </div>
        </div>
      </div>

      <div class="ui segment">
        <h4 class="ui dividing header">Serviços</h4>
        <table class="ui celled selectable tablet stackable table">
          <thead v-if="$isDesktop">
            <tr>
              <th>Serviço</th>
              <th>Laudos</th>
              <th>Valor Total</th>
              <th>Total Recebido</th>
              <th v-if="hasAnticipation">Total Antecipado</th>
              <th>Total a Receber</th>
            </tr>  
          </thead>
          <tbody>
            <tr v-for="(service, index) in resume">                            
              <td>
                <strong class="mobile-only">Serviço: </strong> 
                {{ service.description }}
                <span class="ui small label m-l-10">
                  <i :class="service.typeIcon"></i>
                  {{ service.typeDescription }}
                </span>
              </td>              
              <td>
                <strong class="mobile-only">Laudos: </strong> {{ $formatInteger(service.count) }}   
              </td>
              <td>
                <strong class="mobile-only">Valor Total: </strong> R$ {{ $formatCurrency(service.value) }}
              </td>
              <td>
                <strong class="mobile-only">Total Recebido: </strong> R$ {{ $formatCurrency(service.receivedValue) }}
              </td>
              <td v-if="hasAnticipation">
                <strong class="mobile-only">Total Antecipado: </strong> R$ {{ $formatCurrency(service.anticipationValue) }}
              </td>
              <td>
                <strong class="mobile-only">Total a Receber: </strong> R$ {{ $formatCurrency(service.invoiceableValue) }}
              </td>              
            </tr>                
          </tbody>
          <tfoot>
            <tr>
              <td></td>
              <td class="bold text">{{ $formatInteger(totals.count) }}</td>
              <td class="bold text">R$ {{ $formatCurrency(totals.value) }}</td>
              <td class="bold text">R$ {{ $formatCurrency(totals.receivedValue) }}</td>
              <td v-if="hasAnticipation" class="bold text">R$ {{ $formatCurrency(totals.anticipationValue) }}</td>
              <td class="bold text">R$ {{ $formatCurrency(totals.invoiceableValue) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div class="ui segment">
        <h4 class="ui dividing header">Laudos</h4>
        <table class="ui celled selectable tablet stackable table">
          <thead v-if="$isDesktop">
            <tr>
              <th>Número</th>
              <th>Data</th>
              <th>Animal</th>
              <th>Tutor</th>
              <th>Colaborador</th>
              <th>Valor do Laudo</th>
              <th>Valor Recebido</th>
              <th v-if="hasAnticipation">Valor Antecipado</th>
              <th>Valor a Receber</th>
              <th></th>
            </tr>  
          </thead>
          <tbody>
            <tr v-for="cert in invoice.certs" :class="{ negative: cert.fdata.status == 'pending' }">
              <td>
                <strong class="mobile-only">Número: </strong> 
                <i :class="cert.financialStatusData.icon"></i> 
                {{ cert.id }}
              </td>              
              <td>
                <strong class="mobile-only">Data: </strong> {{ $formatDate(cert.date) }}
              </td>
              <td>
                <strong class="mobile-only">Animal: </strong>
                <i v-if="cert.animal.sex == 'male'" class="mars blue icon"></i>
                <i v-if="cert.animal.sex == 'female'" class="venus pink icon"></i>
                {{ cert.animal.name.trim() }}
                <div v-if="cert.animalRegistration.registrationId" class="ui small label">
                  {{ cert.animalRegistration.registrationId }}
                </div> 
              </td>
              <td>
                <strong class="mobile-only">Tutor: </strong> 
                {{ cert.owner.name }}                
              </td>               
              <td>
                <strong class="mobile-only">Colaborador: </strong>
                {{ cert.user.name }}
              </td>
              <td>
                <strong class="mobile-only">Valor do Laudo: </strong> R$ {{ $formatCurrency(cert.value) }}   
              </td>
              <td>
                <strong class="mobile-only">Valor Recebido: </strong> R$ {{ $formatCurrency(cert.fdata.receivedValue) }}
              </td>
              <td v-if="hasAnticipation">
                <strong class="mobile-only">Valor Antecipado: </strong> R$ {{ $formatCurrency(cert.anticipationValue) }}
                <payment-type v-if="cert.anticipationValue" :paymentType="cert.anticipationType" :iconOnly="true" class="m-l-5" />
              </td>
              <td>
                <strong class="mobile-only">Valor a Receber: </strong> R$ {{ $formatCurrency(cert.fdata.invoiceableValue) }}   
              </td>
              <td class="details-cell">
                <button class="ui small icon button" @click="showCert(cert.id)">
                  <i class="search plus icon"></i>
                </button>
              </td>
            </tr>                
          </tbody>          
        </table>
      </div>

    </div>

    <yes-no-modal ref="confirmRemovalModal" danger="true"
      :message="`Confirma exclusão do fechamento ${invoice.id} ?`"  @onYes="onConfirmRemoval()" />

    <yes-no-modal ref="confirmStatusUpdateModal" :danger="isApproved"
      :message="statusUpdateConfirmMessage"  @onYes="onConfirmStatusUpdate()" />

  </div>

</template>

<script>

  import _ from "underscore"  
  import CustomerInvoiceService from "@/services/customer-invoice-service"
  import CertService from "@/services/cert-service"
  import SrvService from "@/services/srv-service"  
  import ReceivableService from "@/services/receivable-service"
  import { AppBrowser, moment } from "@/utils"
  import { BackButtonLink, PaymentType, YesNoModal } from "@/components"

  export default {
    
    name: "ShowCustomerInvoicePage",

    data() {
      return {
        loading: true,
        updatingStatus: false,
        removing: false,        
        hasAnticipation: false,
        service: new CustomerInvoiceService(),
        invoice: {},
        authToken: "",
        resume: [],
        totals: {},
      }
    },

    components: {
      BackButtonLink,
      PaymentType,
      YesNoModal,
    },

    created() {
      this.loadInvoice()
    },

    computed: {

      isWaiting() {
        return (this.invoice.status == "waiting")
      },

      isApproved() {
        return (this.invoice.status == "approved")
      },

      statusUpdateConfirmMessage() {
        return this.isWaiting ?
               `Confirma aprovação do fechamento ${this.invoice.id} ?` :
               `Confirma estorno da aprovação do fechamento ${this.invoice.id} ?`
      },

      pdfLink() {

        let url = `${process.env.VUE_APP_API_URL}/customer-invoices/${this.invoice.id}/pdf?authToken=${this.authToken}`

        return url
      },
    },

    methods: {

      async loadInvoice() {

        this.loading = true

        try {

          this.invoice = await this.service.findById(this.$route.params.id)

          if (!this.authToken)
            this.authToken = await this.$store.state.auth.fbUser.getIdToken(true)

          this.invoice.statusData = CustomerInvoiceService.STATUS[this.invoice.status]

          let certs = this.invoice.certs.slice()
        
          for (let cert of certs) {

            if (!cert.fdata)
              cert.fdata = { status: "uninformed" }

            if (cert.anticipationValue)
              this.hasAnticipation = true

            cert.statusData = CertService.STATUS[cert.status]
            cert.financialStatusData = CertService.FINANCIAL_STATUS[cert.fdata.status]
            cert.serviceTypeData = SrvService.TYPE_ICONS[cert.serviceType.icon]
            cert.sortKey = moment(cert.date).format("YYYYMMDD") + cert.id.toString()
          }

          this.invoice.certs = _.sortBy(certs, "sortKey")

          this.invoice.receivables.forEach((receivable) => {
            receivable.statusData = ReceivableService.getStatus(receivable)
            receivable.isExpired = ReceivableService.isExpired(receivable)
            receivable.typeData = ReceivableService.TYPES[receivable.type]
          })

          this.loadResume()
          
        } catch (error) {
          
          this.$errorHandler(error)
        
        } finally {

          this.loading = false
        }
      },

      loadResume() {

        let service = null        

        for (const cert of this.invoice.certs) {

          service = this.resume.find((srv) => srv.id == cert.serviceId && srv.typeId == cert.serviceTypeId)

          if (service) {
            
            service.count++
            service.value += cert.fdata.certValue            
            service.receivedValue += cert.fdata.receivedValue
            service.anticipationValue += cert.anticipationValue
            service.invoiceableValue += cert.fdata.invoiceableValue

          } else {

            this.resume.push({
              count: 1,
              value: cert.fdata.certValue,              
              receivedValue: cert.fdata.receivedValue,
              anticipationValue: cert.anticipationValue,
              invoiceableValue: cert.fdata.invoiceableValue,
              id: cert.serviceId,
              typeId: cert.serviceTypeId,
              description: cert.service.description,
              typeDescription: cert.serviceType.description,
              typeIcon: cert.serviceTypeData.icon,
              sortKey: cert.service.description.trim().toLowerCase() + cert.serviceType.description.trim().toLowerCase(),
            })
          }

          this.resume = _.sortBy(this.resume, "sortKey")

          this.loadTotals()
        }
      },

      loadTotals() {

        let totals = { count: 0, value: 0, receivedValue: 0, invoiceableValue: 0, anticipationValue: 0 }

        for (const service of this.resume) {
          totals.count += service.count
          totals.value += service.value
          totals.receivedValue += service.receivedValue
          totals.invoiceableValue += service.invoiceableValue
          totals.anticipationValue += service.anticipationValue
        }

        this.totals = totals
      },

      weekDayLabel(date) {
        return moment(date).format("dddd").toLowerCase()
      },

      showCert(id) {        
        this.$router.push(`/procs/certs/${id}/show?historyBack=true`)
      },

      async onConfirmRemoval() {

        if (this.removing)
          return

        this.removing = true
        
        try {

          await this.service.remove(this.invoice)

          this.$reloadDataTables(["customer-invoices"])

          this.$msg.success("Fechamento excluído com sucesso")

          this.$historyBack()
          
        } catch (error) {

          this.$errorHandler(error)
           
        } finally {

          this.removing = false
        }
      },

      async onConfirmStatusUpdate() {

        if (this.updatingStatus)
          return

        this.updatingStatus = true
        
        try {

          if (this.isWaiting) {

            await this.service.approve(this.invoice.id)

            this.$msg.success("Fechamento aprovado")

          } else {

            if (this.hasEmployeeAnticipations())
              return this.$msg.warn("A aprovação desse fechamento não pode ser estornada porque existem baixas de títulos a receber associadas com fechamentos de colaboradores")

            await this.service.disapprove(this.invoice.id)

            this.$msg.success("Aprovação estornada")
          }

          await this.loadInvoice()          

          this.$reloadDataTables(["customer-invoices", "receivables"])
          
        } catch (error) {
          
          this.$errorHandler(error)
        
        } finally {

          this.updatingStatus = false
        }
      },

      hasEmployeeAnticipations() {

        for (const receivable of this.invoice.receivables) {
          for (const settle of receivable.settles) {
            if (settle.employeeInvoiceId)
              return true
          }
        }

        return false          
      },
    },
  }
</script>